import { useCompanyCandidateOptionListQuery } from "@generated/graphql";
import { DownloadOutlined, Person2Outlined } from "@mui/icons-material";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "assets/icons/CloseIcon";
import SearchIcon from "assets/icons/SearchIcon";
import CircularLoading from "components/CircularLoading";
import FileUploadButton from "components/buttons/FileUploadButton";
import Form from "components/form/Form";
import { CANDIDATES_TEMPLATE_FILE_PATH } from "constants/urls";
import { debounce } from "lodash";
import { CandidatesFileInput } from "pages/Testing/view/types";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import CandidatesTable from "./components/Table";

export type AddCandidateModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: ({ selected, formData }: { selected: string[]; formData?: CandidatesFileInput }) => void;
  candidatesUploadButton?: boolean;
  title?: string;
  dialogProps?: DialogProps;
  withCandidatesList?: boolean;
  submitButtonProps?: LoadingButtonProps;
};

const AddCandidateModal = ({
  open,
  onClose,
  onSubmit,
  candidatesUploadButton = false,
  withCandidatesList = false,
  dialogProps,
  submitButtonProps,
}: AddCandidateModalProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const form = useForm<CandidatesFileInput>({ values: { candidatesFile: undefined } });
  const { handleSubmit, watch } = form;
  const candidatesFile = watch("candidatesFile");

  const {
    loading: candidatesLoading,
    data: candidatesData,
    refetch,
  } = useCompanyCandidateOptionListQuery({
    variables: { page: 0, limit: 15 },
    skip: !withCandidatesList,
  });

  const debounceQuery = useCallback(
    debounce((value) => {
      refetch({ filter: { fullName: value }, page: 0, limit: 15 });
    }, 500),
    []
  );

  const submitHandler = (formData: CandidatesFileInput) => {
    onSubmit({ selected, formData });
    onClose();
  };

  const fileUploadButton = (
    <FileUploadButton
      name='candidatesFile'
      label='Загрузить кандидатов'
      accept='.xlsx'
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={withCandidatesList ? "md" : "sm"}
      {...dialogProps}>
      <Form form={form}>
        <Stack
          justifyContent='space-between'
          alignItems='center'
          flexDirection='row'>
          <Box>
            {candidatesUploadButton && (
              <Tooltip title='Шаблон загрузки кандидатов'>
                <IconButton
                  component='a'
                  href={CANDIDATES_TEMPLATE_FILE_PATH}
                  target='_blank'>
                  <DownloadOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <DialogTitle
            variant='h5'
            sx={{ fontWeight: 500, paddingBottom: 0 }}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              useFlexGap>
              {withCandidatesList ? "Выбор кандидатов" : "Загрузка кандидатов"}
              {!!selected.length && (
                <Stack
                  direction='row'
                  alignItems='center'>
                  <Person2Outlined />
                  <Typography sx={{ fontWeight: 500, fontSize: 18 }}>{selected.length}</Typography>
                </Stack>
              )}
            </Stack>
          </DialogTitle>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent>
          {withCandidatesList && (
            <>
              <Typography
                textAlign='center'
                sx={{ marginBottom: 1 }}>
                Укажите кандидатов, которых необходимо назначить на&#160;групповое тестирование
              </Typography>
              <TextField
                name='search'
                placeholder='Поиск'
                variant='outlined'
                color='secondary'
                fullWidth
                onChange={(e) => debounceQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              {candidatesLoading ? (
                <CircularLoading />
              ) : (
                <CandidatesTable
                  data={candidatesData?.companyCandidates.data}
                  selected={selected}
                  setSelected={setSelected}
                />
              )}
            </>
          )}
          {candidatesUploadButton && !withCandidatesList && <Stack sx={{ mt: 2 }}>{fileUploadButton}</Stack>}
        </DialogContent>
        <Stack
          justifyContent='space-between'
          alignItems='flex-end'
          flexDirection='row'>
          {candidatesUploadButton && withCandidatesList && fileUploadButton}
          <DialogActions sx={{ ml: "auto" }}>
            <Button
              variant='outlined'
              onClick={onClose}>
              Отменить
            </Button>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit(submitHandler)}
              disabled={!selected.length && !candidatesFile}
              {...submitButtonProps}>
              {withCandidatesList ? "Добавить" : "Загрузить"}
            </LoadingButton>
          </DialogActions>
        </Stack>
      </Form>
    </Dialog>
  );
};

export default AddCandidateModal;
